import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'

interface UserProfile {
  id: string
  email: string
  role: 'user' | 'admin' | 'super_admin'
  full_name?: string
  subscription_tier: string
  subscription_status: string
}

export function useAdminAuth() {
  const { user } = useAuth()
  const [isAdmin, setIsAdmin] = useState(false)
  const [isSuperAdmin, setIsSuperAdmin] = useState(false)
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const checkAdminStatus = async (retryCount = 0) => {
      if (!user) {
        setIsAdmin(false)
        setIsSuperAdmin(false)
        setUserProfile(null)
        setLoading(false)
        return
      }

      try {
        const { data, error } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('id', user.id)
          .single()

        if (error) {
          // Network error or temporary issue - retry once
          if (retryCount === 0 && error.message?.includes('Failed to fetch')) {
            console.warn('Retrying user profile fetch...')
            setTimeout(() => checkAdminStatus(1), 1000)
            return
          }

          console.error('Error fetching user profile:', error)
          setIsAdmin(false)
          setIsSuperAdmin(false)
          setUserProfile(null)
        } else if (data) {
          setUserProfile(data)
          setIsAdmin(data.role === 'admin' || data.role === 'super_admin')
          setIsSuperAdmin(data.role === 'super_admin')
        }
      } catch (error) {
        console.error('Error in admin check:', error)
      } finally {
        setLoading(false)
      }
    }

    checkAdminStatus()
  }, [user])

  return {
    isAdmin,
    isSuperAdmin,
    userProfile,
    loading,
    userRole: userProfile?.role || 'user'
  }
}

// 管理者アクションログを記録する関数
export async function logAdminAction(
  actionType: string,
  targetUserId?: string,
  details?: Record<string, any>
) {
  try {
    // 現在のユーザーIDを取得
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      console.warn('No authenticated user for logging admin action')
      return
    }

    const { error } = await supabase
      .from('admin_action_logs')
      .insert({
        admin_id: user.id, // 明示的にadmin_idを設定
        action_type: actionType,
        target_user_id: targetUserId,
        details: details || {},
        ip_address: null // クライアント側ではIPアドレスは取得しない
      })

    if (error) {
      console.error('Error logging admin action:', error)
    }
  } catch (error) {
    console.error('Error in logAdminAction:', error)
  }
}